import {checkIsPylatinoCourse, checkIsSpanishCourse} from 'utils/courseUtils';
// Type-only on purpose: applicationData reaches the Firestore client SDK, which
// a value import would pull into every module using these route helpers.
import type {
  ApplicationType,
  ApplicationCard,
  SectionLeadTodoItem,
  StudentTodoItem,
} from '../../firebase/models/applicationData';
import {useLocation, useMatch, useParams} from 'react-router-dom';
import {SECTION_TEACH_SCENARIO_ID} from 'course/application/sectionLeaderApplication/sectionteaching/sectionTeachingFlow';
import type {CourseId} from 'types/common';
import type {LanguageCode} from 'components/translation/utils';
import type {AssignmentId} from 'assignments/types';
import type {UserLessonsProgressMap} from 'course/lessons/userLessonsProgressMap';
import type {UserAssignmentMap} from 'firebase/models/userAssignmentMap';
import type {UserAssignmentProgressMap} from 'assignments/models/userAssignmentProgressMapInterfaces';
import type {ApplicantData} from 'course/application/firebase/models/applicantData';

/**
 * Parent (hosting) course id for the Probability for AI (PAI) application. PAI
 * cohorts (e.g. `pai1`) hang off this parent, which owns the application
 * materials and the slides-surface section-teaching route (`mathteachable`).
 *
 * TODO: [#3518](https://github.com/CodeinPlace/cip/issues/3518) - read the
 * parent from the course document's `parentCourseId` instead of hardcoding it.
 */
export const PAI_PARENT_COURSE_ID: CourseId = 'pai';

/**
 * Background image shown on an application splash page when the course has
 * not set a custom `url` on its `ApplicationData` doc. Used by both the
 * student/section-leader splash pages and the application editor, so the
 * editor can preview the same fallback an admin would otherwise only
 * discover by visiting the live splash page.
 */
export const DEFAULT_APPLICATION_SPLASH_BACKGROUND_URL = '/splash3.jpeg';

/**
 * Builds the course-name text shown above the "{Role} Application" title on the
 * application splash pages. The full name is shown on every viewport (it is
 * short enough to fit on mobile), so this only normalizes whitespace.
 *
 * @param name - The human-readable course name (`CourseDocument.name`), or
 *   undefined when the course has none. When omitted or blank, an empty string
 *   is returned so callers can hide the line.
 * @return The trimmed course name, or an empty string when `name` is missing or
 *   blank.
 */
export function getCourseTitleName({
  name,
}: {
  name: string | undefined;
}): string {
  return name?.trim() ?? '';
}

export interface AllApplicationData {
  appData: ApplicantData | null;
  userAssignmentMap: UserAssignmentMap | null;
  userAssignmentProgress: UserAssignmentProgressMap | null;

  /**
   * Teaching demo video URL. Only populated for section-leader applications.
   */
  videoUrl?: string;
  /**
   * Whether the applicant has completed a teachable agent attempt. Only used
   * for section leaders.
   */
  slTeachingComplete?: boolean;
  /**
   * Whether the applicant has completed a simulated *section* teaching attempt.
   * Only used for section leaders. Not populated in Phase 1 (no completion
   * marker is written yet), so it stays undefined until Phase 2 adds one.
   */
  sectionTeachingComplete?: boolean;

  /**
   * Lessons progress, keyed by lesson id. Populated for both student and
   * section-leader applications, since either timeline may include lesson
   * todos. Absent when the applicant has no lessons-progress doc yet.
   */
  lessonsProgress?: UserLessonsProgressMap;

  /**
   * Time spent on the assignments, mapped by assignment id.
   */
  timeOnAssignments?: Record<AssignmentId, number>;
}

export const getReferralText = (
  targetCourseId: CourseId,
  applicationType: ApplicationType,
): string => {
  if (checkIsPylatinoCourse(targetCourseId)) {
    switch (applicationType) {
      case 'sl':
        return 'Estoy postulando a PYLATINO. Ven a enseñar conmigo: ';
      case 'student':
        return 'Estoy postulando a PYLATINO. Ven a aprender conmigo: ';
    }
  }
  switch (applicationType) {
    case 'sl':
      return "I am applying to Stanford's Python course. Come teach with me: ";
    case 'student':
      return "I am applying to Stanford's Python course. Come learn with me: ";
  }
};

/**
 * Whether an application's due date has passed. The application is open over
 * the closed interval `(-inf, dueDate]` — the due instant itself still counts
 * as on time — and past due over `(dueDate, +inf)`.
 *
 * @param params.dueDate - The due date as an ISO string. Omitted, empty or
 *   unparsable → never past due, so a course without a date stays open.
 * @param params.now - The current time, ideally server time. Undefined (server
 *   time not yet known) → not past due.
 * @return True once `now` is strictly after `dueDate`.
 * @throws Never.
 */
export const isPastDueDate = ({
  dueDate,
  now,
}: {
  dueDate?: string;
  now: Date | number | undefined;
}): boolean => {
  if (now === undefined) {
    return false;
  }
  if (!dueDate) {
    return false;
  }
  const due = new Date(dueDate).getTime();
  if (isNaN(due)) {
    return false;
  }
  return new Date(now).getTime() > due;
};

export interface DueDateDisplayParts {
  /**
   * The due date in the viewer's own local time zone, e.g.
   * "Sep 30, 12:59 PM Pacific Time". Derived from the runtime's default time
   * zone, which reflects the OS's configured zone rather than the system
   * clock — this stays correct even when the viewer's clock itself is wrong.
   * Uses `Intl`'s "generic" zone name (`timeZoneName: 'longGeneric'`) rather
   * than a short abbreviation (e.g. "PDT") or a DST-specific name (e.g.
   * "Pacific Daylight Time") so it reads the same name year-round and is
   * spelled out for every viewer worldwide, not just US zones — e.g. "Eastern
   * Time", "India Standard Time", "Central European Time", "West Africa
   * Time".
   */
  localText: string;
  /**
   * The same instant expressed as an Anywhere-on-Earth (UTC-12) cutoff, e.g.
   * "Sep 30, 7:59 AM AoE" — the deadline hasn't truly passed until this time,
   * everywhere in the world.
   */
  aoeText: string;
}

/**
 * Builds a short due-date display: the instant in the viewer's own local
 * time zone (primary, for readability) plus the same instant as an
 * Anywhere-on-Earth (AoE) cutoff (secondary, for the global fairness
 * guarantee). Drops the weekday and only includes the year when `isoDate`
 * falls in a different calendar year than `now`, so the common case reads as
 * a short "Mon Day, H:MM AM/PM ZONE" string rather than a full sentence.
 *
 * @param isoDate - The due date as an absolute-instant ISO string.
 * @param language - The locale to format with. Defaults to `'en'`.
 * @param now - The current time, used only to decide whether to include the
 *   year. Defaults to `new Date()`.
 * @returns The local-time and AoE display strings.
 */
export const getDueDateDisplayParts = ({
  isoDate,
  language = 'en',
  now = new Date(),
}: {
  isoDate: string;
  language?: LanguageCode;
  now?: Date;
}): DueDateDisplayParts => {
  const date = new Date(isoDate);
  const includeYear = date.getFullYear() !== now.getFullYear();
  const sharedParts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? {year: 'numeric' as const} : {}),
    hour: 'numeric',
    minute: '2-digit',
  };

  const localText = date.toLocaleString(language, {
    ...sharedParts,
    timeZoneName: 'longGeneric',
  });

  // Anywhere on Earth (AoE) = UTC-12; IANA uses Etc/GMT+12 for UTC-12.
  const aoeText = `${date.toLocaleString(language, {
    ...sharedParts,
    timeZone: 'Etc/GMT+12',
  })} AoE`;

  return {localText, aoeText};
};

// Anywhere on Earth (AoE) = UTC-12; ISO's `-12:00` offset suffix pins an
// absolute instant to that wall-clock time regardless of the reader's zone.
const AOE_UTC_OFFSET = '-12:00';

/**
 * Converts an absolute-instant ISO due-date string into the value an
 * Anywhere-on-Earth (AoE) `datetime-local` input should show, e.g.
 * `2026-10-01T07:59:00.000Z` becomes `2026-09-30T19:59`. Lets admins author
 * application deadlines directly in the same AoE frame students see them in,
 * instead of picking an arbitrary UTC offset and mentally converting.
 *
 * @param isoDate - An absolute-instant ISO string, or `''`/unparsable input.
 * @returns A `YYYY-MM-DDTHH:MM` value, or `''` if `isoDate` is empty or
 *   unparsable — `datetime-local` treats `''` as blank.
 */
export const isoDateToAoeDatetimeLocalValue = (isoDate: string): string => {
  if (!isoDate) {
    return '';
  }
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) {
    return '';
  }
  // `hourCycle: 'h23'` is required over `hour12: false`: the latter renders
  // AoE midnight as hour "24" attributed to the *previous* calendar day
  // (an ICU quirk), which would silently roll the displayed date back by one.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Etc/GMT+12',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find(p => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

/**
 * Converts a `datetime-local` value entered as Anywhere-on-Earth (AoE)
 * wall-clock time into the absolute-instant ISO string the course document
 * stores, by suffixing it with the fixed AoE (UTC-12) offset. The inverse of
 * {@link isoDateToAoeDatetimeLocalValue}.
 *
 * @param datetimeLocalValue - A `YYYY-MM-DDTHH:MM` value from a
 *   `datetime-local` input, interpreted as AoE wall-clock time.
 * @returns The absolute-instant ISO string to store.
 */
export const aoeDatetimeLocalValueToIsoDate = (
  datetimeLocalValue: string,
): string => {
  return `${datetimeLocalValue}:00.000${AOE_UTC_OFFSET}`;
};

/**
 * The local time of day a results release is pinned to when an admin picks a
 * day but not a time: midday in the COURSE's own timezone.
 *
 * A results date is a real moment — results go live at some instant, and from
 * that instant every applicant can see them. Storing an actual instant is what
 * lets {@link formatResultsDay} render the day the release lands in each
 * applicant's own calendar, which is the true answer for them: a release at
 * midday in California is already the next morning in Tokyo, so Tokyo should be
 * told the later day rather than California's.
 *
 * Midday rather than midnight so the stored moment sits far from a date
 * boundary in the course's own timezone, and so an admin who never picks an
 * exact time still gets a sensible one. Refine it in the Admin Editor, which
 * writes the same field with a full time and offset.
 */
const RESULTS_DAY_LOCAL_HOUR = 12;

/**
 * The timezone a results day is interpreted in when the course document does
 * not set one. Every current course sets `timezone`; this only guards a course
 * created without it.
 */
const FALLBACK_RESULTS_TIME_ZONE = 'America/Los_Angeles';

/**
 * Resolves a timezone's UTC offset on a given instant, as an ISO suffix.
 *
 * @param date - The instant to resolve the offset at; it varies with DST.
 * @param timeZone - An IANA timezone name.
 * @returns An ISO offset such as `-07:00`, or `+00:00` for UTC itself.
 * @throws Never for a valid IANA name; an invalid one propagates the
 *   `RangeError` that `Intl.DateTimeFormat` throws.
 */
function isoOffsetFor(date: Date, timeZone: string): string {
  const name = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'longOffset',
  })
    .formatToParts(date)
    .find(part => part.type === 'timeZoneName')?.value;
  // `longOffset` renders as `GMT-07:00`, and as bare `GMT` at zero offset.
  const offset = (name ?? '').replace('GMT', '');
  return offset === '' ? '+00:00' : offset;
}

/**
 * Converts a stored results-date ISO string into the `YYYY-MM-DD` value a
 * `date` input should show, read in the course's timezone — the same frame
 * {@link resultsDayValueToIsoDate} writes in, so a value this editor saved
 * round-trips exactly.
 *
 * Legacy values written by the Admin Editor (an arbitrary wall-clock time and
 * offset) are shown as the day they fall on in the course's timezone, and
 * re-saving normalizes them to {@link RESULTS_DAY_LOCAL_HOUR}.
 *
 * @param isoDate - An absolute-instant ISO string, or `''`/unparsable input.
 * @param timeZone - The course's IANA timezone. Omitted →
 *   {@link FALLBACK_RESULTS_TIME_ZONE}.
 * @returns A `YYYY-MM-DD` value, or `''` when `isoDate` is empty or
 *   unparsable — a `date` input treats `''` as blank.
 * @throws Never.
 */
export const isoDateToResultsDayValue = (
  isoDate: string,
  timeZone: string = FALLBACK_RESULTS_TIME_ZONE,
): string => {
  if (!isoDate) {
    return '';
  }
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) {
    return '';
  }
  // `en-CA` formats as YYYY-MM-DD, which is exactly a date input's value.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

/**
 * Converts a `YYYY-MM-DD` value from a `date` input into the absolute-instant
 * ISO string the course document stores, pinned to
 * {@link RESULTS_DAY_LOCAL_HOUR} in the course's timezone. The inverse of
 * {@link isoDateToResultsDayValue}.
 *
 * @param dayValue - A `YYYY-MM-DD` value from a `date` input.
 * @param timeZone - The course's IANA timezone. Omitted →
 *   {@link FALLBACK_RESULTS_TIME_ZONE}.
 * @returns The ISO string to store, or `''` when `dayValue` is empty — so
 *   clearing the field clears the stored date rather than storing a bogus one.
 * @throws Never.
 */
export const resultsDayValueToIsoDate = (
  dayValue: string,
  timeZone: string = FALLBACK_RESULTS_TIME_ZONE,
): string => {
  if (!dayValue) {
    return '';
  }
  const hour = String(RESULTS_DAY_LOCAL_HOUR).padStart(2, '0');
  // The offset depends on the instant (DST), so resolve it at the target day's
  // midday UTC — far enough from any transition (they run near 02:00 local)
  // that it resolves to the same offset as the intended local midday.
  const offset = isoOffsetFor(new Date(`${dayValue}T12:00:00.000Z`), timeZone);
  return `${dayValue}T${hour}:00:00.000${offset}`;
};

/**
 * Formats a stored results date as the calendar day the release lands on in the
 * READER's own timezone.
 *
 * Local, not UTC, and deliberately: the stored value is the instant results go
 * live, so the day it falls on differs by where you are, and each applicant's
 * own day is the true answer for them. A release at midday in California is
 * already the next morning in Tokyo, and telling Tokyo the Californian date
 * would point them at a day when the results are not up yet.
 *
 * @param isoDate - An absolute-instant ISO string, or `''`/unparsable input.
 * @param language - BCP-47 tag used for month/day naming and ordering.
 * @param monthFormat - `'long'` for "October", `'short'` for "Oct". Omitted →
 *   `'long'`.
 * @returns The formatted day, or `null` when `isoDate` is empty or unparsable,
 *   so callers can fall back to date-free copy.
 * @throws Never.
 */
export const formatResultsDay = (
  isoDate: string | undefined,
  language: string,
  monthFormat: 'long' | 'short' = 'long',
): string | null => {
  if (!isoDate) {
    return null;
  }
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString(language, {
    year: 'numeric',
    month: monthFormat,
    day: 'numeric',
  });
};

export const timezoneMatcher = (date: string): string => {
  // get last 5 characters of date
  const timezone = date.slice(-6);

  // if they match the pattern +/=##:##, return the timezone
  if (timezone.match(/[+-]\d{2}:\d{2}/)) {
    switch (timezone) {
      case '-11:00':
        return '';
      case '-07:00':
        return 'PDT';
      case '-08:00':
        return 'PST';
      case '-04:00':
        return 'EDT';
      case '-05:00':
        return 'EST';
      default:
        return timezone;
    }
  }
};

/**
 * The "parent course" in an application is the course that hosts the
 * application and the materials (lessons, assignments, etc.) for the
 * application. It is NOT the course that the user is applying to. For that, use
 * `useTargetCourseId`.
 *
 * This course is something like "public" or "pylatino-publico" for the English
 * and Spanish courses, respectively.
 */
export const getParentCourseId = (courseId: CourseId): CourseId => {
  // Temporary safety net: pai1's parent is pai, not public. The proper fix is to
  // read the authoritative parentCourseId field on the course document (see
  // parentCourseId in packages/core .../documents/course.ts) rather than
  // guessing here.
  // TODO: resolve parent from the course document instead of hardcoding.
  if (courseId === 'pai1') {
    return PAI_PARENT_COURSE_ID;
  }
  // This may need to be adapted as the course structure changes / more languages added
  return checkIsSpanishCourse(courseId) ? 'pylatino-publico' : 'public';
};

/**
 * Checks whether `courseId` names one of the application parent courses — the
 * courses that host application materials (see {@link getParentCourseId})
 * rather than accept applicants. A parent course is never a valid application
 * target, so an apply link pointing at one (e.g. `/apply/pai/student`) is a
 * broken link.
 *
 * @param courseId - The course id to test.
 * @return True when `courseId` is a parent course id.
 */
export const checkIsParentCourseId = (courseId: CourseId): boolean =>
  courseId === PAI_PARENT_COURSE_ID ||
  courseId === 'public' ||
  courseId === 'pylatino-publico';

/**
 * The "target course" in an application is the course that the user is applying
 * to. It is NOT the course that hosts the application and the materials
 * (lessons, assignments, etc.) for the application. For that, use
 * `getParentCourseId`.
 *
 * This course is something like "cip5" or "pylatino-25".
 */
export const useTargetCourseId = (): CourseId => {
  const {targetCourseId} = useParams();
  return targetCourseId;
};

/**
 * Reads which application flow a pathname belongs to, across both URL shapes:
 *
 * - long form:  `/:courseId/applyteach/:target/…` → `'sl'`,
 *               `/:courseId/join/:target/…` → `'student'`
 * - short link: `/apply/:target/sl/…` → `'sl'`,
 *               `/apply/:target/student/…` → `'student'`
 *
 * Subpages and trailing slashes are recognized (`/apply/pai1/sl/aboutJob`,
 * `/apply/pai1/sl/`). This is the ONE place that knows how to read the
 * application type off a URL — layouts, gates, and forms must call it rather
 * than pattern-matching pathnames themselves, so the two URL shapes can never
 * disagree about which application a page belongs to.
 *
 * @param pathname - A location pathname.
 * @return `'sl'` or `'student'` for application routes, `null` for any other
 *   pathname.
 */
export function getApplicationTypeFromPath(
  pathname: string,
): ApplicationType | null {
  if (
    pathname.includes('/applyteach/') ||
    /^\/apply\/[^/]+\/sl(\/|$)/.test(pathname)
  ) {
    return 'sl';
  }
  if (
    pathname.includes('/join/') ||
    /^\/apply\/[^/]+\/student(\/|$)/.test(pathname)
  ) {
    return 'student';
  }
  return null;
}

/**
 * Attempts to determine the application type from the current URL. Thin hook
 * wrapper around {@link getApplicationTypeFromPath}.
 *
 * @return `'sl'` or `'student'` on application routes, `null` if the current
 *   URL is not an application route.
 */
export const useApplicationType = (): ApplicationType | null => {
  const {pathname} = useLocation();
  return getApplicationTypeFromPath(pathname);
};

/**
 * Whether the current URL is the application short-link shape
 * (`/apply/:targetCourseId/…`).
 *
 * Links emitted from inside an application flow should follow the URL shape
 * the user is already on: both shapes serve the same pages, but navigating
 * across shapes crosses route subtrees, which remounts the whole
 * `ApplicationJoinLayout` (re-auth + refetch) instead of swapping the
 * layout's `<Outlet/>` in place (see `CourseScopeLayout` in Router.tsx).
 * Pass the result as `preferShortLink` to {@link getAppSplashRoute} /
 * {@link getTodoItemRoute}.
 *
 * @return True when the current URL is under `/apply/…`, false otherwise.
 */
export function useIsApplyShortLinkRoute(): boolean {
  return useMatch('/apply/:targetCourseId/*') !== null;
}

/**
 * Builds the canonical splash route for an application.
 *
 * By default this is the long form `/<parent>/<join|applyteach>/<target>`,
 * except for PAI-parented courses, whose real, persistent address is the
 * short link `/apply/<target>/<student|sl>` (see `ApplyRouteGate` and
 * `CourseScopeLayout` in Router.tsx). Other courses can opt into the short
 * link via the course document's `applyShortLinkEnabled` flag — the redirect
 * components keep both shapes working either way, and the short-link route
 * subtree mirrors the long form's sub-pages (about, aboutJob, teachingDemo,
 * ...), so callers may append sub-paths onto the result under either shape
 * (see `getTodoItemRoute`, the application editors).
 *
 * @param courseId - The target course id (the course being applied to).
 * @param applicationType - Whether this is a student (`join`) or section-leader
 *   (`applyteach`) application; selects the route segment.
 * @param parentCourseId - The parent/hosting course that owns the current URL
 *   (e.g. `pai` or `public`). Pass this from `useParams().courseId` so the
 *   generated route stays under the same parent the user is already browsing.
 *   When omitted (as it is on the short link, whose URL carries no
 *   `:courseId`), the parent is derived from the target course via
 *   {@link getParentCourseId}.
 * @param preferShortLink - Pass true to force the short-link form; callers
 *   inside an application flow should pass
 *   {@link useIsApplyShortLinkRoute}'s result so links preserve the URL shape
 *   the user is on. When omitted or false, the form is chosen by the
 *   PAI-parent default described above.
 * @returns The splash route string.
 */
export function getAppSplashRoute(
  courseId: CourseId,
  applicationType: ApplicationType,
  parentCourseId?: CourseId,
  preferShortLink?: boolean,
): string {
  const parent = parentCourseId ?? getParentCourseId(courseId);
  if (preferShortLink || parent === PAI_PARENT_COURSE_ID) {
    return `/apply/${courseId}/${applicationType}`;
  }
  const applicationTypeRoute = applicationType === 'sl' ? 'applyteach' : 'join';
  return `/${parent}/${applicationTypeRoute}/${courseId}`;
}

const APPLY_COURSE_PARAM = 'apply';
const APPLY_ROLE_PARAM = 'applyRole';

function isApplicationType(value: string | null): value is ApplicationType {
  return value === 'sl' || value === 'student';
}

/**
 * Marks a task URL as belonging to an application, so a task rendered outside
 * the application's route subtree can still find its way back. Read the markers
 * with {@link getApplicationTaskExitRoute}.
 *
 * @param to - The task's app-relative destination. Anything else (an absolute
 *   or protocol-relative URL) is returned unchanged rather than rewritten,
 *   which would retarget it at this origin.
 * @param courseId - The course being applied to.
 * @param applicationType - Which of the course's applications this task is in.
 * @return `to` with both markers set, replacing any already on it.
 * @throws Never.
 */
export function withApplicationTaskParams({
  to,
  courseId,
  applicationType,
}: {
  to: string;
  courseId: CourseId;
  applicationType: ApplicationType;
}): string {
  if (!to.startsWith('/') || to.startsWith('//')) {
    return to;
  }
  // The base is only here so URL can do the query/fragment bookkeeping; it is
  // never part of the result.
  const url = new URL(to, 'https://app.invalid');
  url.searchParams.set(APPLY_COURSE_PARAM, courseId);
  url.searchParams.set(APPLY_ROLE_PARAM, applicationType);
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * The application home a task should return to, read off the markers
 * {@link withApplicationTaskParams} wrote.
 *
 * @param search - A query string, with or without its leading `?`.
 * @param parentCourseId - The parent course owning the current URL, forwarded
 *   to {@link getAppSplashRoute}. When omitted it is derived from the target
 *   course, which collapses non-public parents to `public`.
 * @return The splash route, or null when both markers are not present and
 *   valid, leaving the caller its own destination. Always the canonical splash
 *   route — a course served at the short link is redirected there on arrival by
 *   `LegacyApplicationSplashRedirect`.
 * @throws Never.
 */
export function getApplicationTaskExitRoute({
  search,
  parentCourseId,
}: {
  search: string;
  parentCourseId?: CourseId;
}): string | null {
  const params = new URLSearchParams(search);
  const targetCourseId = params.get(APPLY_COURSE_PARAM);
  const applicationType = params.get(APPLY_ROLE_PARAM);
  if (!targetCourseId || !isApplicationType(applicationType)) {
    return null;
  }
  return getAppSplashRoute(targetCourseId, applicationType, parentCourseId);
}

/**
 * Resolves the route a splash todo item links to.
 *
 * Some todo types are rendered at hardcoded application routes rather than a
 * stored `to` value (see `TYPES_WITH_DEFAULT_TO_LINK`): for those the route is
 * derived from {@link getAppSplashRoute}, and the item's own `to` is used only
 * as the fallback for every other type. This mirrors how the shared splash
 * checklist (`TodoCards`) has always resolved links, so every splash renderer
 * navigates consistently and does not silently break if an admin omits `to`
 * on a hardcoded-route type.
 *
 * @param item - The todo item to resolve a route for.
 * @param courseId - The target course id (e.g. from the route params).
 * @param applicationType - Whether this is a student or section-leader
 *   application; selects the splash route base.
 * @param parentCourseId - The parent/hosting course that owns the current URL
 *   (e.g. `pai` or `public`), forwarded to {@link getAppSplashRoute}. Pass this
 *   from `useParams().courseId` so the resolved route stays under the same
 *   parent the user is browsing. When omitted, the parent is derived from the
 *   target course, which collapses non-public parents (like `pai`) to `public`.
 * @param preferShortLink - Forwarded to {@link getAppSplashRoute}. Pass
 *   {@link useIsApplyShortLinkRoute}'s result so in-flow routes preserve the
 *   URL shape the user is on. When omitted, the PAI-parent default applies.
 * @returns The route to navigate to, or `null` when the type has no hardcoded
 *   route and the item has no `to` value.
 */
export function getTodoItemRoute({
  item,
  courseId,
  applicationType,
  parentCourseId,
  preferShortLink,
}: {
  item: StudentTodoItem | SectionLeadTodoItem;
  courseId: CourseId;
  applicationType: ApplicationType;
  parentCourseId?: CourseId;
  preferShortLink?: boolean;
}): string | null {
  const baseRoute = getAppSplashRoute(
    courseId,
    applicationType,
    parentCourseId,
    preferShortLink,
  );
  // Captured before the switch so the `default` arm can still read it. Every
  // known TodoType now has a case, which narrows `item` to `never` there — but
  // the arm has to stay: todo items are authored data read from Firestore, so an
  // unrecognized `type` must fall back to the item's own destination rather than
  // returning undefined.
  const authoredDestination = item.to ?? null;
  switch (item.type) {
    case 'form':
      return `${baseRoute}/about`;
    case 'record':
      return `${baseRoute}/teachingdemo`;
    case 'debugging':
    case 'aboutCourse':
    case 'aboutJob':
    case 'simteaching':
      return `${baseRoute}/${item.type}`;
    case 'sectionteaching-pai':
      // PAI applications run the section-teaching practice on the SLIDES
      // surface — the `mathteachable` route nested in this splash subtree — not
      // the coding IDE version below. Staying in-subtree means entering it from
      // the checklist swaps only the layout's <Outlet/> (no full-page reload).
      // The scenario/deck are baked in for now (see PaiSectionTeachingRoute).
      // TODO: [#3518](https://github.com/CodeinPlace/cip/issues/3518) - drive
      // this off the course's teaching surface instead of a hardcoded route.
      return `${baseRoute}/mathteachable`;
    case 'sectionteaching': {
      // Section "Practice Teaching" runs on the merged course-scoped route
      // (it needs the IDE), not under the applyteach splash subtree — so it
      // always lives under the LONG-form parent prefix, even when the rest of
      // the flow is served at the short link. Derive the prefix from the
      // long form explicitly (baseRoute may be the short form).
      //
      // This resolves the IDE-based exercise, which is what every course
      // except CIPX uses. CIPX runs the slides-based one and overrides this
      // in its own `CipxTodoList` — see `cipxSectionTeachingRoute` there.
      const longFormRoute = getAppSplashRoute(
        courseId,
        applicationType,
        parentCourseId,
      );
      const parentPrefix = longFormRoute.split('/applyteach/')[0];
      return `${parentPrefix}/applyteach-sectionteach/${SECTION_TEACH_SCENARIO_ID}?apply=${courseId}`;
    }
    case 'lesson':
    case 'code':
      // Both viewers sit outside this application's route subtree — the lesson
      // viewer on `/{parent}/learn/{lessonId}`, the coding exercise on the IDE's
      // `/{parent}/ide/a/{assnId}` — so the markers are the only way their exit
      // affordances can find the checklist.
      // A falsy `to` passes through untouched: `''` is how a placeholder item is
      // authored to render non-clickable, and TodoCards links it without a guard.
      if (!item.to) {
        return authoredDestination;
      }
      return withApplicationTaskParams({
        to: item.to,
        courseId,
        applicationType,
      });
    default:
      return authoredDestination;
  }
}

/**
 * The path to a custom message for the splash page.
 */
export const getSplashCustomMessageTiptapFirebasePath = (
  courseId: CourseId,
  applicationType: ApplicationType,
): string => {
  return `course/${getParentCourseId(courseId)}/docs/application-data/role/${applicationType}/tiptaps/custom-message`;
};

/**
 * Extracts the required assignment for the given todo item. The caller should
 * verify that the provided item is of type `code` before calling this function.
 *
 * The assignment id is the last path segment of `item.to`. Falls back to
 * `item.id` when the item has no usable `to` (`to` is optional on
 * `BaseTodoItem`), so a misconfigured todo item reads as an unknown/incomplete
 * assignment instead of throwing and taking down the splash page.
 *
 * @param item - The code todo item.
 * @returns The assignment id to look up in the user's assignment map.
 */
export const getAssignmentIdFromUrl = (
  item: StudentTodoItem | SectionLeadTodoItem,
): AssignmentId => {
  const match = item.to?.match(/([^/]+)$/);
  return match?.[1] ?? item.id;
};

/**
 * The lesson id a lesson todo item points at.
 *
 * The canonical id is the last path segment of `item.to` (the
 * `/…/learn/<lessonId>` route), which is the same id lesson progress is
 * recorded under. A todo item's own `id` is only a display slug and may not
 * match the lesson id — e.g. CIPX uses `print-lesson` for a lesson whose id is
 * `cipx-print` — so it must not be used for completion lookups. Falls back to
 * `item.id` only when the item has no `to`.
 *
 * @param item - The lesson todo item.
 * @returns The lesson id to look up in the user's lessons-progress map.
 */
export const getLessonIdFromItem = (
  item: StudentTodoItem | SectionLeadTodoItem,
): string => {
  const match = item.to?.match(/([^/]+)$/);
  return match?.[1] ?? item.id;
};

/**
 * Returns all todo items of the given type from the given cards.
 *
 * For example, to get all lessons from the given cards, you can do:
 *
 * ```
 * const lessons = getTodoItemsOfType(cards, 'lesson');
 * ```
 */
export const getTodoItemsOfType = <
  T extends StudentTodoItem | SectionLeadTodoItem,
>(
  cards: ApplicationCard<T>[],
  type: T['type'],
): T[] => {
  return cards.flatMap(card => {
    return card.todo.filter(item => item.type === type);
  });
};
